// The DASHBOARD — the platform state and services every rdrive dashboard runs on
// (IA_ABSORPTION_PLAN Phase 1 step 6b-2b). createDashboard(config) creates exactly one per app
// from its config, provides it, and components reach it with useDashboard(). Until 6b-2b all of
// this lived in project-overview's Vuex store and App/setup.js.
//
//   boot context  params (usePlatformParams, parsed once), missingParams, settings (reactive),
//                 setValues (the URL values → settings, locale normalised), setScopedToken
//   transport     fetcher (THE one proxy fetcher, over live settings), api (ApiClient over it)
//   DuckDB        duckdb (DuckDBService), conn, db, isDuckDBInitialized, initDuckDB()
//   data          loader (createDataLoader over config.data.manifest), dataLoaded
//   filters       filters (one context per config.filters entry), newFilterContext(name)
//   analytics     analytics (createAnalytics from config.analytics, else a no-op)
//   lifecycle     start() — the platform half of the boot; stop()
//
// CREATING it starts nothing: no request, no DuckDB, no token refresh, no analytics call. The
// app's root component calls `dashboard.start()` once it is mounted (project-overview:
// App/setup.js's onMounted), which validates the URL FIRST — a missing required param stops
// there, before anything starts — then writes the settings, configures analytics, logs the user
// id, arms the token refresh and initialises DuckDB, in that order (the order App/setup.js's
// parseQuerystring + onMounted had). The data load itself stays the app's call
// (`dashboard.loader.load()`), because WHEN to load is app UI (project-overview: the welcome
// dialog's Load button).
//
// No store, no SQL, no table names (PLATFORM.md §3.2 rules 1–2): the manifest, the filter
// configs and the onLoaded hook arrive in the config, as app code.
import { markRaw, reactive, ref, shallowReactive, shallowRef } from 'vue';
import { ApiClient, createProxyFetcher } from '@rdrive/data-transport';
import { DuckDBService } from '@rdrive/data-duckdb';
import { createDataLoader } from '@rdrive/data-loader';
import { createFilterContext } from '@rdrive/data-filters';
import { createAnalytics } from '@rdrive/helper-analytics';
import {
  createHostBridge,
  createTokenRefresher,
  getJwtExpiryMs,
  resolveHostOrigin,
} from '@rdrive/helper-host-bridge';
import { DEFAULT_LOCALE, normalizeLocale } from '@rdrive/i18n';
import { usePlatformParams } from './usePlatformParams.js';

const PREFIX = '@rdrive/plugin-dashboard-shell';

/** What `dashboard.analytics` is when the config has no `analytics.measurementId`. */
const NO_ANALYTICS = Object.freeze({ init() {}, trackEvent() {}, trackPage() {} });

/** rdrive's GA context on every hit: the API host the dashboard talks to (`dimension1`). */
const defaultAnalyticsContext = ({ settings }) => ({ parent_url: settings.apiUrl });

/**
 * @typedef {Object} DashboardSettings  reactive; THE platform settings object
 * @property {string|null} proxyUrl     the integration proxy host (config.transport.proxyUrl)
 * @property {string|null} apiUrl       the API host: config.transport.apiUrl until start()
 *   writes the URL's `apiUrl`
 * @property {string|null} projectCode
 * @property {string|null} projectName
 * @property {string|null} scopedToken  rotated in place by setScopedToken (token refresh)
 * @property {*} userId                 usePlatformParams' policy (the token's claim first)
 * @property {string} locale            normalised — always one of SUPPORTED_LOCALES
 */

/**
 * @typedef {Object} Dashboard
 * @property {import('./usePlatformParams.js').PlatformParams} params
 * @property {import('vue').Ref<string[]>} missingParams  set by start(): the required params the
 *   URL lacks ([] until then, and when it is complete)
 * @property {DashboardSettings} settings
 * @property {(values: Object) => void} setValues  apiUrl, projectCode, projectName, scopedToken,
 *   locale (normalised) → settings — start()'s write, in that order
 * @property {(token: string) => void} setScopedToken  THE write path for a rotated token
 * @property {Function} fetcher        createProxyFetcher over live settings
 * @property {Object} api              ApiClient over `fetcher` (markRaw)
 * @property {Object} duckdb           the DuckDBService (markRaw)
 * @property {Object} conn             duckdb.conn — stable; answers empty until init + data load
 * @property {import('vue').ShallowRef<Object|null>} db  the AsyncDuckDB once initialised
 * @property {import('vue').Ref<boolean>} isDuckDBInitialized
 * @property {() => Promise<{ success: boolean, error?: * }>} initDuckDB
 * @property {Object} loader           createDataLoader's { load, ensure, reload, progress, dataLoaded }
 * @property {import('vue').Ref<boolean>} dataLoaded  = loader.dataLoaded
 * @property {Record<string, Object>} filters  shallowReactive: one filter context per config entry
 * @property {(name: string) => Object} newFilterContext  a FRESH, uninitialised context built from
 *   the same config entry (not installed — assign it to `filters[name]` to replace)
 * @property {{ init: Function, trackEvent: Function, trackPage: Function }} analytics
 * @property {() => Promise<boolean>} start  the platform half of the boot (once; later calls
 *   return the same promise): false when a required URL param is missing, true otherwise
 * @property {() => void} stop  stops the token refresher
 */

/**
 * Build the dashboard from createDashboard's config. Pure construction — see the file header.
 *
 * @param {Object} config  createDashboard's config; this reads `params`, `transport`, `data`,
 *   `filters`, `analytics` and `dev`
 * @returns {Dashboard}
 */
export function createDashboardContext(config = {}) {
  const {
    params: paramsOptions,
    transport = {},
    data = {},
    filters: filterConfigs = {},
    analytics: analyticsConfig,
    dev = false,
  } = config;

  // --- boot context ----------------------------------------------------------------------
  // The URL, parsed once (the host recreates the iframe rather than change it). The user id is
  // a getter: the token is decoded on its first read, which is start()'s.
  const params = usePlatformParams(paramsOptions);
  // A ref (deep) on purpose: start() logs and the shell's dialog lists the same reactive array
  // project-overview's App state held before 6b-2b.
  const missingParams = ref([]);

  const settings = reactive({
    proxyUrl: transport.proxyUrl ?? null,
    apiUrl: transport.apiUrl ?? null,
    projectCode: null,
    projectName: null,
    scopedToken: null,
    userId: null,
    locale: DEFAULT_LOCALE,
  });

  // The URL-borne values, in the order project-overview's store `setValues` wrote them. THE one
  // normalisation point for `locale` (normalizeLocale from @rdrive/i18n): every reader — the
  // root i18n's locale watch, the app's getters, date formatting — gets a supported locale.
  const setValues = (values) => {
    settings.apiUrl = values.apiUrl;
    settings.projectCode = values.projectCode;
    settings.projectName = values.projectName;
    settings.scopedToken = values.scopedToken;
    settings.locale = normalizeLocale(values.locale);
  };

  // THE write path for a rotated token: the fetcher re-reads settings on every request, so this
  // one assignment reaches the ApiClient and every DuckDB loader at once.
  const setScopedToken = (token) => {
    settings.scopedToken = token;
  };

  // --- transport -------------------------------------------------------------------------
  // ONE proxy fetcher for the whole dashboard, over a GETTER on live settings (re-read per
  // request — the token-refresh seam, @rdrive/data-transport). Both the REST client and DuckDB
  // get this same fetcher.
  const fetcher = createProxyFetcher(() => ({
    proxyUrl: settings.proxyUrl,
    apiUrl: settings.apiUrl,
    scopedToken: settings.scopedToken,
  }));
  // markRaw: services, not state — a store or component holding them in reactive state gets the
  // instance itself, never a proxy of it.
  const api = markRaw(new ApiClient({ fetcher }));

  // --- DuckDB ----------------------------------------------------------------------------
  const duckdb = markRaw(new DuckDBService());
  const db = shallowRef(null);
  const isDuckDBInitialized = ref(false);

  const initDuckDB = async () => {
    try {
      // The shared fetcher carries proxyUrl/apiUrl/scopedToken and re-reads them per request,
      // so nothing is captured here.
      const success = await duckdb.initialize({ fetcher });
      isDuckDBInitialized.value = success;
      db.value = duckdb.db;
      return { success };
    } catch (error) {
      console.error('Error initializing DuckDB:', error);
      isDuckDBInitialized.value = false;
      return { success: false, error };
    }
  };

  // --- analytics ---------------------------------------------------------------------------
  const analytics = analyticsConfig?.measurementId
    ? createAnalytics({
        measurementId: analyticsConfig.measurementId,
        load: analyticsConfig.load,
        // read at send time, so a hit carries the settings of that moment
        getContext: () => (analyticsConfig.getContext ?? defaultAnalyticsContext)(dashboard),
      })
    : NO_ANALYTICS;

  const dashboard = {
    params,
    missingParams,
    settings,
    setValues,
    setScopedToken,
    fetcher,
    api,
    duckdb,
    // Stable: DuckDBService builds its `conn` in the constructor and never replaces it; it reads
    // the live connection per call (answering empty results until init and the data load).
    conn: duckdb.conn,
    db,
    isDuckDBInitialized,
    initDuckDB,
    analytics,
  };

  // --- filter contexts -----------------------------------------------------------------------
  // One per config entry: a createFilterContext config (from @rdrive/data-filters; `conn` is
  // supplied here, read per call) or a factory `(dashboard) => context` for an app with its own
  // context implementation (issue-analysis used it for its own classes until IA_ABSORPTION_PLAN
  // Phase 4 step 11; no app does now).
  const filterFactories = {};
  for (const [name, definition] of Object.entries(filterConfigs)) {
    if (typeof definition === 'function') {
      filterFactories[name] = () => definition(dashboard);
    } else if (definition && typeof definition === 'object') {
      filterFactories[name] = () => createFilterContext({ ...definition, conn: () => duckdb.conn });
    } else {
      throw new Error(`${PREFIX}: filters.${name} must be a createFilterContext config or a factory function.`);
    }
  }
  // shallowReactive: replacing `filters[name]` (the latent replacement path) is seen by every
  // computed that reads it; the contexts themselves are markRaw and never proxied.
  dashboard.filters = shallowReactive(
    Object.fromEntries(Object.entries(filterFactories).map(([name, make]) => [name, make()])),
  );
  dashboard.newFilterContext = (name) => {
    const make = filterFactories[name];
    if (!make) throw new Error(`${PREFIX}: no filter context "${name}" in the config's \`filters\`.`);
    return make();
  };

  // --- data loader -----------------------------------------------------------------------------
  // ONE loader over the app's manifest and this DuckDB. `params` is the live settings object, read
  // at every load (`{projectCode}`). The app's `onLoaded` runs after the boot tables and ddl.post,
  // with the query gate open, and FINISHES before `dataLoaded` flips (the R2 boot-race fix).
  const { manifest = { tables: [] }, onLoaded, onProgress } = data;
  const hooks = {};
  if (onLoaded) {
    hooks.onLoaded = (context) => onLoaded({ ...context, dashboard, filters: dashboard.filters });
  }
  if (onProgress) hooks.onProgress = onProgress;
  dashboard.loader = createDataLoader({ duckdb, manifest, params: () => settings, hooks });
  dashboard.dataLoaded = dashboard.loader.dataLoaded;

  // --- the boot (platform half) ------------------------------------------------------------------
  let tokenRefresher = null;

  // HOST_APP_CONVENTIONS §3.2: the host mints the URL token once per iframe creation, so long
  // sessions need a refresh. Disabled automatically when not embedded or the host origin cannot
  // be established — the dashboard then runs on its boot token.
  const startTokenRefresh = () => {
    const hostOrigin = resolveHostOrigin();
    const hostBridge = createHostBridge({ hostOrigin });

    if (!hostBridge.available) {
      console.info('Scoped-token refresh disabled: no host origin (running unembedded or origin not resolvable)');
    } else {
      // Positive indicator for testing against the real host: without it the console is silent
      // until the first rotation (~5 min before expiry)
      const expiryMs = getJwtExpiryMs(settings.scopedToken);
      const expiry = expiryMs ? new Date(expiryMs).toLocaleTimeString() : 'unknown';
      console.info(`Scoped-token refresh armed: host ${hostOrigin}, token expires ${expiry}, refresh ~5 min before`);
    }

    tokenRefresher = createTokenRefresher({
      getToken: () => settings.scopedToken,
      setToken: setScopedToken,
      bridge: hostBridge,
    });
    tokenRefresher.start();

    // DEV-only hook (config.dev — the app passes import.meta.env.DEV) so the smoke harness can
    // drive a refresh on demand (tooling/smoke/10-token-refresh.js)
    if (dev) {
      window.__rdriveDev = {
        ...(window.__rdriveDev || {}),
        refreshToken: () => tokenRefresher.refreshNow(),
        hostBridge,
      };
    }
  };

  const boot = async () => {
    // 1. Validate FIRST. A missing required param (usePlatformParams' `missing`: absent or empty)
    //    stops the boot here — no settings, no analytics, no token refresh, no DuckDB — while the
    //    shell's missing-params dialog lists it.
    missingParams.value = params.missing;
    if (missingParams.value.length > 0) {
      console.error('Missing required parameters:', missingParams.value);
      return false;
    }

    // 2. The URL values → settings (locale normalised).
    setValues({
      scopedToken: params.scopedToken,
      projectName: params.projectName,
      projectCode: params.projectCode,
      locale: params.locale,
      apiUrl: params.apiUrl,
    });

    // 3. GA `config` once the URL settings are in (its context reads settings.apiUrl).
    analytics.init();

    // 4. The user id — usePlatformParams' policy: the token's `rdrive_user_id` claim is
    //    authoritative, the host's `userId` param only a fallback (a differing param → the claim
    //    + one console.warn). The token is decoded here, on this first read.
    const userId = params.userId;
    console.log('User ID:', userId);
    settings.userId = userId;

    // 5. Token refresh.
    startTokenRefresh();

    // 6. DuckDB, with the shared fetcher. A failed init leaves isDuckDBInitialized false; the
    //    boot still counts as started (the URL was complete), as it always did.
    await initDuckDB();
    return true;
  };

  let started = null;
  dashboard.start = () => {
    if (!started) started = boot();
    return started;
  };

  dashboard.stop = () => {
    if (tokenRefresher) {
      tokenRefresher.stop();
      tokenRefresher = null;
    }
  };

  // markRaw like the loader and the filter contexts: held in reactive state, the dashboard keeps
  // its refs as refs.
  return markRaw(dashboard);
}
